import { api } from "@/lib/api";

// Collaborators & workspace-switcher data layer (Epic 10.3/10.4). Thin axios
// wrappers over the /api/collaborators/* contract, plus pure helpers that the
// UI and unit tests share. Every screen degrades gracefully when an endpoint is
// not live yet (parallel backend work) — callers treat a 404 as "empty".

export const COLLAB_LIMIT = 3;

// ── API ─────────────────────────────────────────────────────────────────────
export const listCollaborators = () => api.get("/collaborators");
export const inviteCollaborator = (email) => api.post("/collaborators/invite", { email });
export const resendInvite = (id) => api.post(`/collaborators/${id}/resend`);
export const revokeCollaborator = (id) => api.delete(`/collaborators/${id}`);

export const listInvites = () => api.get("/collaborators/invites");
export const acceptInvite = (id) => api.post(`/collaborators/${id}/accept`);
export const declineInvite = (id) => api.post(`/collaborators/${id}/decline`);

// Workspaces the current user can switch into (their ACTIVE grants as a
// collaborator). The switch itself lives in AuthContext.switchWorkspace so it
// can refetch /auth/me in the same step (POST /collaborators/switch).
export const listWorkspaces = () => api.get("/collaborators/workspaces");

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

const norm = (s) => (s || "").toString().trim().toUpperCase();

// A grant counts against the limit while it is INVITED or ACTIVE (REVOKED and
// DECLINED are dead and free the slot again).
export function liveGrants(list) {
  return (list || []).filter((c) => {
    const s = norm(c?.status);
    return s === "INVITED" || s === "ACTIVE";
  });
}

export function liveGrantCount(list) {
  return liveGrants(list).length;
}

export function remainingSlots(list, limit = COLLAB_LIMIT) {
  return Math.max(0, limit - liveGrantCount(list));
}

export function canInvite(list, limit = COLLAB_LIMIT) {
  return liveGrantCount(list) < limit;
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
}

export function normalizeEmail(email) {
  return (email || "").trim().toLowerCase();
}

// Visual chip metadata per grant status. `tone` maps to a brand color family the
// component turns into classes.
export function statusChip(status) {
  switch (norm(status)) {
    case "ACTIVE":
      return { label: "Ativo", tone: "sage" };
    case "INVITED":
      return { label: "Convidado", tone: "gold" };
    case "REVOKED":
      return { label: "Revogado", tone: "muted" };
    case "DECLINED":
      return { label: "Recusado", tone: "muted" };
    default:
      return { label: status || "—", tone: "muted" };
  }
}

// pt-BR short date (dd/mm/aaaa). Returns "" for missing/invalid input so the UI
// can omit the "· <date>" tail cleanly.
export function formatDatePtBr(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Build the account-switcher option list: the user's own account first, then any
// shared workspaces. `active` marks the option matching the current context so
// the UI can render a check + suppress a redundant switch call.
export function buildAccountOptions(user, workspaces) {
  const active = user?.active_workspace || null;
  const own = {
    id: null,
    kind: "own",
    label: "Minha conta",
    sublabel: user?.name || user?.email || "",
    role: "owner",
    active: !active,
  };
  const shared = (workspaces || [])
    .filter((w) => w && w.owner_user_id != null)
    .map((w) => ({
      id: w.owner_user_id,
      kind: "shared",
      label: w.marca || w.name || "Marca compartilhada",
      sublabel: w.owner_name || w.owner_email || "",
      role: w.role || "viewer",
      active: Boolean(active) && active.owner_user_id === w.owner_user_id,
    }));
  return [own, ...shared];
}
